import React, { useState } from "react";
import "../Table/Table.css";
import toast, { Toaster } from 'react-hot-toast';
import Loading from "../Loading";
import {
  Box,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchActivity } from "../../api/activityApi";
import DataTableSecond from "../DataTable/DataTableSecond";
import { useErrorToast } from "../../hooks/useErrorToast";

function ActivityTab() {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [filters, setFilters] = useState({
    action: "",
    from_date: "",
    to_date: "",
  });

  const hasActiveFilters =
    filters.action || filters.from_date || filters.to_date;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      action: "",
      from_date: "",
      to_date: "",
    });
    setPage(1);
    refetch();
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["activityLogs", page, perPage, filters],
    queryFn: () => fetchActivity(page, perPage, filters),
    keepPreviousData: true,
    onError: (error) => {
      if (error.response?.status === 401) {
        showError("Session Expired! Please Login");
        localStorage.removeItem("tokenInv");
        localStorage.removeItem("roleInv");
        navigate("/");
      } else {
        showError(error?.response?.data?.error || "Something went wrong. Please try again.");
        // toast.error(error.response?.data?.error || "Something went wrong");
      }
    },
  });

  const logs =
    data?.logs.map((log) => ({
      id: log.id,
      action: log.action
        ? log.action.charAt(0).toUpperCase() + log.action.slice(1)
        : "N/A",
      user_agent: log.user_agent || "N/A",
      table: log.table_name || "N/A",
      method: log.method || "N/A",
      endpoint: log.endpoint || "N/A",
      user: log.user?.name || "-",
      createdAt: log.created_at
        ? new Date(log.created_at).toLocaleString()
        : "N/A",
      ip: log.ip_address || "N/A",
    })) || [];

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "action", headerName: "Action", width: 120 },
    { field: "user_agent", headerName: "User Agent", width: 120 },
    { field: "endpoint", headerName: "Endpoint", width: 200 },
    { field: "table", headerName: "Table", width: 150 },
    { field: "user", headerName: "User", width: 180 },
    { field: "createdAt", headerName: "Timestamp", width: 200 },
    { field: "ip", headerName: "IP Address", width: 150 },
  ];

  const totalPages = data?.pages || 1;

  return (
    <div style={{ padding: "20px" }}>
      <Toaster position="top-right" reverseOrder={false} style={{ zIndex: "9999999" }}/>
      {(isLoading || isFetching) && <Loading />}
      <div className="table-header">
        <h2>Activity Logs</h2>
      </div>

      {/* Filters */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2} alignItems="center">
        <FormControl variant="outlined" size="small">
          <InputLabel id="action-label">Action</InputLabel>
          <Select
            labelId="action-label"
            name="action"
            value={filters.action}
            onChange={handleFilterChange}
            label="Action"
            style={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="create">Create</MenuItem>
            <MenuItem value="update">Update</MenuItem>
            <MenuItem value="delete">Delete</MenuItem>
          </Select>
        </FormControl>

        <input
          type="date"
          name="from_date"
          value={filters.from_date}
          onChange={handleFilterChange}
          style={{
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        <input
          type="date"
          name="to_date"
          value={filters.to_date}
          onChange={handleFilterChange}
          style={{
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        {hasActiveFilters && (
          <Button variant="outlined" color="secondary" onClick={handleResetFilters}>
            Reset Filters
          </Button>
        ) }
      </Box>

      {/* Table */}
      <div style={{ marginBottom: "30px" }}>
        <DataTableSecond columns={columns} rows={logs} height={400} />
      </div>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Box>
    </div>
  );
}

export default ActivityTab;
